import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef, useCallback } from 'react';
import { CategoryService } from '../services/category.service';
import { QuestionBankService } from '../services/question-bank.service';
import { QuestionCategory } from '../types/question-bank.types';

/**
 * Hook for managing question bank items using React Query
 */
export const useQuestionBank = (page = 1, limit = 10) => {
  // Local loading state to ensure UI shows loading indicator
  const [localLoading, setLocalLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [categories, setCategories] = useState<QuestionCategory[]>([]);
  
  // Track current page to prevent unnecessary refetches
  const pageRef = useRef(page);

  // Get query client for cache invalidation
  const queryClient = useQueryClient();

  // Query key for question bank items - use a base key without page for invalidation
  const questionBankBaseKey = ['question-bank-items'];
  const questionBankQueryKey = [...questionBankBaseKey, page, limit];

  // Query key for categories
  const categoriesQueryKey = ['question-bank-categories'];

  // Update page reference when page changes
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Fetch question bank items with pagination
  const {
    data: itemsData,
    isLoading: isItemsLoading,
    error: itemsError,
    refetch: refetchItems,
  } = useQuery({
    queryKey: questionBankQueryKey,
    queryFn: async () => {
      setLocalLoading(true);
      try {
        console.log(`Fetching items for page ${page}`);
        // Using static method directly with pagination
        const data = await QuestionBankService.getQuestionBankItems(
          page,
          limit
        );
        setTotalItems(data.total);
        return data.items;
      } finally {
        setLocalLoading(false);
      }
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 0, // Set to 0 to always refetch when page changes
    gcTime: 0,
  });

  // Initialize category service
  const categoryService = new CategoryService();

  // Fetch categories
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: categoriesQueryKey,
    queryFn: async () => {
      try {
        // Using instance method
        return await categoryService.getCategoryHierarchy();
      } catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }
    },
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  // Update categories state when data changes
  useEffect(() => {
    if (categoriesData) {
      setCategories(categoriesData);
    }
  }, [categoriesData]);

  // Custom refresh function that ensures loading state is visible
  const refresh = useCallback(async () => {
    setLocalLoading(true);

    // Only refetch categories if they don't exist or are empty
    if (!categories || categories.length === 0) {
      await Promise.all([refetchItems(), refetchCategories()]);
    } else {
      // Just refetch items if categories are already loaded
      await refetchItems();
    }

    setLocalLoading(false);
  }, [categories, refetchItems, refetchCategories]);

  // Delete mutation
  const deleteMutation = useMutation({
    // Using static method directly
    mutationFn: (id: string) => QuestionBankService.deleteQuestionBankItem(id),
    onSuccess: () => {
      console.log('Question deleted successfully');
      // Invalidate query to trigger refetch
      queryClient.invalidateQueries({ queryKey: questionBankBaseKey });
      // Ensure loading state is visible
      setLocalLoading(true);
    },
  });

  // Helper function to find a category by ID in the hierarchy
  const findCategoryById = useCallback(
    (id: string, cats: QuestionCategory[]): QuestionCategory | null => {
      for (const cat of cats) {
        if (cat.id === id) return cat;
        if (cat.subcategories && cat.subcategories.length > 0) {
          const found = findCategoryById(id, cat.subcategories);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  // Helper function to get category name by ID
  const getCategoryNameById = useCallback(
    (categoryId: string): string => {
      const category = findCategoryById(categoryId, categories);
      if (!category) return 'Unknown Category';
      return categoryService.getCategoryPath(category, categories);
    },
    [categories, findCategoryById]
  );

  // Delete question function
  const deleteQuestion = useCallback(async (id: string) => {
    await deleteMutation.mutateAsync(id);
  }, [deleteMutation]);

  return {
    items: itemsData || [],
    totalItems,
    categories,
    loading: localLoading || isItemsLoading || isCategoriesLoading,
    error: itemsError || categoriesError,
    refresh,
    deleteQuestion,
    getCategoryNameById,
  };
};
